import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

// ── WHICH NUMBERS ALREADY HAVE AN ACCOUNT ────────────────────────────────────
// One empty-ish document per registered phone number, so the sign-up form can
// answer "is this number taken?" BEFORE it pays for an SMS.
//
// WHY NOT `users/{uid}`. The number is deliberately not stored there — see the
// note in userService.js. Firestore grants read access per DOCUMENT and never
// per field, and a designer's or a shop's user document is world-readable, so a
// phone number written onto it would turn the public directory into a phone
// list. That constraint has not changed; this collection exists precisely so
// the number does not have to go anywhere near the profile.
//
// WHAT THIS COLLECTION GIVES AWAY, stated plainly. The check runs before anyone
// is signed in, so the document has to be readable without an account. Anyone
// can therefore ask "does +374… have an account here?" one number at a time —
// which is inherent to the feature, not a flaw in this implementation: a form
// that tells you a number is taken tells whoever asks. The rules narrow it as
// far as it can go: `get` is public, `list` is denied, so a caller can confirm
// a number they already have but cannot enumerate the collection to harvest
// them. Nothing but a timestamp is stored, so a hit reveals nothing beyond the
// answer to the question that was asked.
//
// KEYED BY THE NUMBER, digits only. The document id IS the lookup — a single
// point read, no query, no index, and no way to phrase a broader question.

/** `+37444123456` → `37444123456`. Digits only, so the id has no syntax to it. */
export function phoneDocId(e164) {
  return String(e164 || '').replace(/\D+/g, '');
}

/**
 * Is this number already registered?
 *
 * FAILS OPEN — a thrown read returns `false`, i.e. "go ahead and send". This is
 * a courtesy check that saves an SMS, not a gate: rules not yet deployed, a
 * flaky network or a browser blocking storage must never be able to lock a
 * legitimate person out of signing up. The panel still catches the case after
 * verification (see nextStepAfterVerify), so a false negative costs one message
 * and lands on the same screen.
 */
export async function isPhoneRegistered(e164) {
  const id = phoneDocId(e164);
  if (!id) return false;
  try {
    const snap = await getDoc(doc(db, 'phoneIndex', id));
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * Record that this number has an account.
 *
 * Called after EVERY successful phone sign-in, not only on the sign-up that
 * created the account. That is what backfills the index: every number that
 * registered before this collection existed is unknown to it until its owner
 * next signs in, and writing on each sign-in means they are added the first
 * time they come back rather than never.
 *
 * Never throws and is not awaited by its caller. A failure here means the next
 * sign-up with this number sends one unnecessary SMS — it must not be allowed
 * to fail a sign-in that has already succeeded.
 */
export async function rememberPhone(e164) {
  const id = phoneDocId(e164);
  if (!id) return;
  try {
    await setDoc(doc(db, 'phoneIndex', id), { createdAt: serverTimestamp() }, { merge: true });
  } catch { /* see above — best effort by design */ }
}
